import { api } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtQty, type LotRow } from "@/types/inventory";

const date = (d: string) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });

/** Dye lots / batches with how much of each is left */
export function LotsTab({ search, reloadKey }: { search: string; reloadKey: number }) {
  const { error } = useToast();
  const list = usePagedList<LotRow>({
    deps: [search, reloadKey],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30" });
      if (search.trim()) qs.set("search", search.trim());
      try {
        return unwrapPage<LotRow>(await api.get(`/api/Inventory/Lots?${qs}`));
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load lots");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });

  if (list.loading && list.rows.length === 0) return <TableSkeleton rows={6} columns={6} />;
  return (
    <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Lot</TableHead>
            <TableHead>Product / shade</TableHead>
            <TableHead>Came in on</TableHead>
            <TableHead>Supplier</TableHead>
            <TableHead className="text-right">Rolls left</TableHead>
            <TableHead className="text-right">On hand</TableHead>
            <TableHead className="text-right">Received</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.rows.map((l) => (
            <TableRow key={l.id}>
              <TableCell className="font-semibold text-sm">{l.lot_number}</TableCell>
              <TableCell className="text-sm">
                <div className="font-medium">{l.product_name}</div>
                <div className="text-[11px] text-muted-foreground">{l.variant_name || "No shade"}</div>
              </TableCell>
              <TableCell className="text-sm">
                {date(l.lot_date)}
                {l.source_ref && <div className="text-[11px] text-muted-foreground font-mono">{l.source_ref}</div>}
              </TableCell>
              <TableCell className="text-sm">{l.supplier_name || "-"}</TableCell>
              <TableCell className="text-right text-sm">{l.unit_count}</TableCell>
              <TableCell className="text-right text-sm font-semibold">{fmtQty(l.on_hand_qty, l.unit)}</TableCell>
              <TableCell className="text-right text-sm text-muted-foreground">{fmtQty(l.received_qty, l.unit)}</TableCell>
            </TableRow>
          ))}
          {list.rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-10 text-muted-foreground text-sm">
                No lots yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
    </div>
  );
}
